import React, { useContext, useState, useEffect, useRef } from "react";
import { AuthContext } from "../../../auth/AuthContext";
import HeroArt from "../../../../assets/images/hero-art.png";
import BackgroundWebM from "../../../../assets/images/backGround.webm";
import BackgroundMP4 from "../../../../assets/images/backGround.mp4";
import styles from "./HeroAboutSection.scss";
import { useScrollAnimation } from "../hooks/useScrollAnimation";

export function HeroAboutSection() {
  const auth = useContext(AuthContext);
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState("about");
  const aboutAreaRef = useRef(null);

  // スクロールアニメーション
  const [holeRef, holeVisible] = useScrollAnimation({ threshold: 0.3 });
  const [aboutRef, aboutVisible] = useScrollAnimation({ threshold: 0.2 });

  // フッターからのタブ切り替えイベントをリッスン
  useEffect(() => {
    const handleOpenTab = event => {
      const { tab } = event.detail;
      if (tab && ["about", "guide", "faq", "terms"].includes(tab)) {
        setActiveTab(tab);
        setIsExpanded(true);
        // aboutエリアにスクロール
        setTimeout(() => {
          if (aboutAreaRef.current) {
            aboutAreaRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }, 100);
      }
    };

    window.addEventListener("openAboutTab", handleOpenTab);
    return () => {
      window.removeEventListener("openAboutTab", handleOpenTab);
    };
  }, []);

  const handleCTAClick = () => {
    if (auth.isSignedIn) {
      window.open("/gaYGQMU/エントランスワールド", "_blank");
    } else {
      window.open("/signin", "_blank");
    }
  };

  const handleExpandClick = () => {
    setIsExpanded(!isExpanded);
  };

  const tabContents = {
    about: {
      title: "コンセプト",
      content: (
        <div className={styles.tabContent}>
          <p>
            個人の関心から社会の課題まで、私たちはそれぞれに想いをもっています。
            <br />
            その想いを誰かと共有し、一歩カタチに近づける。
            <br />
            MetaCampUs（メタキャンパス）は、そんな知と創造が交わるオンラインコミュニティです。
            <br />
            <br />
            仲間と語り合い、学び合い、プロジェクトとして試してみる——。
            <br />
            学生も研究者も、会社員も起業家も。
            <br />
            学びや活動の中から生まれる“やってみたい”を、ここMetaCampUsで動かしていこう。
          </p>
        </div>
      )
    },
    guide: {
      title: "操作説明",
      content: (
        <div className={styles.tabContent}>
          <h4>MetaCampUs操作方法</h4>

          <div className={styles.guideSection}>
            <h5>【共通】</h5>
            <ol>
              <li>
                <strong>ブラウザを開く:</strong> お使いのパソコンで、Google Chromeを開きます。
              </li>
              <li>
                <strong>MetaCampUsのウェブサイトにアクセス:</strong>{" "}
                ブラウザのアドレスバーにMetaCampUsのURL（https://metacampus.jp/）を入力してアクセスします。
              </li>
              <li>
                <strong>ルームの作成または参加:</strong>{" "}
                「クリックして入場」をクリックしてエントランスワールドに入室するか、URLなどから既存のルームに参加します。
              </li>
              <li>
                <strong>プロフィールの設定:</strong>{" "}
                初めてルームに入る際は、アイコンや表示名、プロフィール、フレンド限定表示内容、Discord通知、ニックネームなどを設定します。
              </li>
              <li>
                <strong>アバターの選択:</strong> 自分の分身であるアバターを選択します。
              </li>
              <li>
                <strong>マイクとスピーカーの設定:</strong> マイクとスピーカーを設定します。
              </li>
            </ol>
          </div>

          <div className={styles.guideSection}>
            <h5>【パソコンでの操作方法】</h5>
            <div className={styles.guideSubsection}>
              <h6>・移動</h6>
              <p>
                WASDキーで移動、マウスで視点を変更します。スペースバーを押すことで着席アイコンが表示され、クリックする事で着席できます。
              </p>
            </div>
            <div className={styles.guideSubsection}>
              <h6>・UI説明</h6>
              <ul>
                <li>
                  <strong>招待ボタン:</strong> 他の人を招待するためのURLが発行可能です。
                </li>
                <li>
                  <strong>マイクボタン:</strong> マイクとスピーカーの変更またはミュートが行えます。
                </li>
                <li>
                  <strong>共有ボタン:</strong> 自分のカメラまたは画面の共有が可能です。
                </li>
                <li>
                  <strong>アイテムボタン:</strong>{" "}
                  ペンやカメラ、アバターの変更、シーンの変更や画像・動画・PDF等のアップロードが可能です。
                </li>
                <li>
                  <strong>リアクションボタン:</strong> 空間上に飛び出すリアクションスタンプを呼び出すことが可能です。
                </li>
                <li>
                  <strong>チャットボタン:</strong>{" "}
                  チャットを行う事も可能です。Discordにも送信する欄にチェックを入れる事で、Discordにも同時に送信可能です。
                </li>
                <li>
                  <strong>退室ボタン:</strong> ルームからの退室が可能です。ブラウザを閉じる事でも退室が可能です。
                </li>
                <li>
                  <strong>メニューボタン（右下）:</strong>{" "}
                  新規ルームの作成や名前とアバターの変更が可能です。また環境設定やチュートリアル、利用規約の確認も可能です。
                </li>
              </ul>
            </div>
          </div>

          <div className={styles.guideSection}>
            <h5>【スマートフォンでの操作方法】</h5>
            <div className={styles.guideSubsection}>
              <h6>・移動</h6>
              <p>
                画面をスワイプまたは画面右に表示されている半透明のコントローラーをスワイプする事で視点を変更し、画面右に表示されている半透明のコントローラーをスワイプする事で移動します。
              </p>
              <p>画面をマルチタップする事で着席アイコンが表示され、タップする事で着席できます。</p>
            </div>
            <div className={styles.guideSubsection}>
              <h6>・UI説明</h6>
              <ul>
                <li>
                  <strong>マイクボタン:</strong> マイクとスピーカーの変更またはミュートが行えます。
                </li>
                <li>
                  <strong>共有ボタン:</strong> 自分のカメラの共有が可能です。
                </li>
                <li>
                  <strong>アイテムボタン:</strong>{" "}
                  ペンやカメラ、アバターの変更、シーンの変更や画像・動画・PDF等のアップロードが可能です。
                </li>
                <li>
                  <strong>リアクションボタン:</strong> 空間上に飛び出すリアクションスタンプを呼び出すことが可能です。
                </li>
                <li>
                  <strong>チャットボタン（右下）:</strong>{" "}
                  チャットを行う事も可能です。Discordにも送信する欄にチェックを入れる事で、Discordにも同時に送信可能です。
                </li>
                <li>
                  <strong>メニューボタン（左上）:</strong>{" "}
                  新規ルームの作成や名前とアバターの変更が可能です。また環境設定やチュートリアル、利用規約の確認も可能です。
                </li>
              </ul>
              <p>退室するには、ブラウザを閉じてください。</p>
            </div>
          </div>
        </div>
      )
    },
    faq: {
      title: "よくある質問",
      content: (
        <div className={styles.tabContent}>
          <h4>よくある質問</h4>

          <div className={styles.faqSection}>
            <div className={styles.faqItem}>
              <h5 className={styles.faqQuestion}>Q. メタバースって難しくないですか？</h5>
              <p className={styles.faqAnswer}>
                いわば、顔出しの不要なオンライン会議と思ってください。誰でもブラウザからアクセスが可能で、操作も簡単です。初回はナビゲーターがご案内します。
              </p>
            </div>

            <div className={styles.faqItem}>
              <h5 className={styles.faqQuestion}>Q. どのくらいの頻度で活動していますか？</h5>
              <p className={styles.faqAnswer}>
                どのくらいの方が参加されるかによって活動内容や頻度は変わります。理念に沿った活動に対して事務局が抑制することは想定していないため、自発的な活動を促進していきたいと考えています。
              </p>
            </div>
          </div>

          <div className={styles.faqContact}>
            <p>参加方法や技術的な質問など、わからないことがあればお気軽にご連絡ください。</p>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent("openContactModal"))}
              className={styles.faqContactButton}
            >
              お問い合わせフォームへ
            </button>
          </div>
        </div>
      )
    },
    terms: {
      title: "利用規約",
      content: (
        <div className={styles.tabContent}>
          <h4>MetaCampUs Japan 利用規約</h4>

          <div className={styles.termsSection}>
            <h4>0.総則</h4>
            <p>
              MetaCampUs Japan
              (以下「本サービス」といいます。)とは、インターネットが繋がるデバイスでご利用いただける３D空間で通信可能なサービスであり、東京科学大学
              辻本研究室（以下「辻本研究室」と表記する。）により運営されています。本規約は、辻本研究室が提供する本サービスに対して適用され、本サービスの利用に関する条件を、本サービスを利用するユーザー（以下「ユーザー」といいます。）と辻本研究室との間で定めるものです。なお、本規約に基づき、ユーザーと辻本研究室の間で成立する本サービスの利用に関するアカウント登録を以下「本登録」といいます。
            </p>
          </div>

          <div className={styles.termsSection}>
            <h4>1.用語定義</h4>
            <p>本規約では、以下の用語を使用します。</p>
            <ol>
              <li>
                「コンテンツ」とは、文字、記号、文章、音声、音楽、画像、動画、ソフトウェア、プログラム、コードその他の情報のことをいいます。
              </li>
              <li>「本コンテンツ」とは、本サービスを通じてアクセスすることができるコンテンツのことをいいます。</li>
              <li>
                「投稿コンテンツ」とは、ユーザーが本サービスにおいて投稿、登録、送信、受信、アップロード、その他方法を問わず辻本研究室に提供したコンテンツのことをいいます。
              </li>
              <li>
                「投稿等」とは、ユーザーが投稿コンテンツを投稿、登録、送信、アップロード、その他本サービスにおいて提供することを総称していいます。
              </li>
              <li>「送受信」とは、ユーザーが投稿コンテンツを投稿等および受信することをいいます。</li>
              <li>
                「メッセージ機能」とは、本サービスを利用するユーザーがメッセージ内容（音声メッセージ、テキストメッセージ）を辻本研究室及び本サービスを利用する他のユーザーとの間で通信を行うものです。
              </li>
              <li>「提携先等」とは、辻本研究室と提携する事業者、または辻本研究室が業務を委託する事業者をいいます。</li>
              <li>
                「知的財産権等」とは、特許権、実用新案権、意匠権、商標権、著作権、肖像権、プライバシー権、パブリシティ権等の権利を総称していいます。
              </li>
              <li>
                「利用者情報」とは、通信機器または接続機器（端末）の情報、本サービス利用履歴、端末位置情報、クッキー情報等の辻本研究室のウェブサイト等にアクセスした際に取得される履歴情報をいいます。
              </li>
              <li>
                「ID」とは、３Ｄアバターや３Ｄ空間を作成、利用する際に、入力フォームに入力するユーザーのメールアドレスをいいます。
              </li>
            </ol>
          </div>

          <div className={styles.termsSection}>
            <h4>2.本サービスの利用</h4>
            <ol>
              <li>
                <strong>本規約の適用:</strong>{" "}
                辻本研究室は、本規約の他、本サービスの提供画面等に本サービスのご利用条件を定める場合があります。
              </li>
              <li>
                <strong>本サービスの利用:</strong>{" "}
                本サービスの利用にあたっては、本規約及びご利用条件が適用されますので、これらの内容をお読みいただいたうえでご利用ください。
              </li>
              <li>
                <strong>未成年のご利用:</strong>{" "}
                未成年のユーザーが本サービスを利用される場合には、事前に親権者の同意を得るものとします。
              </li>
            </ol>
          </div>

          <div className={styles.termsSection}>
            <h4>3.IDおよびご利用端末の管理</h4>
            <p>
              ユーザーは、ユーザーの責任においてIDおよびご利用端末を管理しなければなりません。ユーザーのID・またはご利用端末が利用された場合は、辻本研究室の責めに帰すべき事由による場合を除き、ユーザーご自身が本サービスを利用されたものとみなし、ユーザーが一切の責任を負うものとします。
            </p>
          </div>

          <div className={styles.termsSection}>
            <h4>4.利用料</h4>
            <p>本サービスは、本規約で別段の定めがある場合を除き、無料でご利用できます。</p>
          </div>

          <div className={styles.termsSection}>
            <h4>5.商用利用</h4>
            <p>
              ユーザーが本サービスを利用して３Ｄ空間を活用した商取引を行う等本サービスを商用に利用する場合、辻本研究室の承認を得るものとします。
            </p>
            <p>連絡先：contact@metacampus.jp</p>
          </div>

          <div className={styles.termsSection}>
            <h4>6.個人情報等の取り扱い</h4>
            <p>
              辻本研究室は、本サービスの提供にあたり、ユーザーから取得する個人情報を、本サービスを含む辻本研究室の実施する起業活動支援プログラムの情報提供、お問い合わせ対応、本サービスの改善を目的に利用します。
            </p>
          </div>

          <div className={styles.termsSection}>
            <h4>7.本コンテンツの利用</h4>
            <p>
              本コンテンツについて、ユーザーは、譲渡および再許諾できず、非独占的な、本サービス上における利用を唯一の目的とする、利用権のみ行使できるものとします。
            </p>
          </div>

          <div className={styles.termsSection}>
            <h4>8.投稿コンテンツの取り扱い</h4>
            <p>本サービスに関して、投稿コンテンツの取り扱いがある場合は、以下の各号によるものとします。</p>
            <ol>
              <li>
                <strong>辻本研究室への投稿コンテンツの利用許諾</strong>
                <br />
                投稿コンテンツに係る著作権は、投稿コンテンツを投稿等したユーザーに帰属します。ユーザーは、辻本研究室が辻本研究室のサービスを運営するために、国内外を問わず、無償、永久かつ非独占的に投稿コンテンツを利用できる権利を辻本研究室に許諾し、かつ、辻本研究室がユーザーから許諾された条件の範囲内で提携先等に対して投稿コンテンツの利用を許諾する権利を辻本研究室に許諾します。辻本研究室は、投稿コンテンツを辻本研究室の定めた仕様・条件等に従い、辻本研究室指定の配置、態様で掲載、表示するものとします。ユーザーは、投稿コンテンツに関して著作者人格権を行使しないものとします。ユーザーは、投稿コンテンツの著作権を第三者に譲渡する場合は、本条に定める条件を譲渡先に遵守させるものとします。また、本サービスの適正な運営のため、投稿コンテンツが非公開である場合も、必要に応じて辻本研究室がその内容を閲覧できるものとします。
              </li>
              <li>
                <strong>辻本研究室等による投稿コンテンツの削除</strong>
                <br />
                辻本研究室は、投稿コンテンツが違法またはその恐れがあると合理的に判断した場合、ユーザーに通知することなくこれを削除できるものとします。
              </li>
              <li>
                <strong>投稿コンテンツのバックアップ</strong>
                <br />
                投稿コンテンツのバックアップは、ユーザーが自らの責任で行うものとし、辻本研究室は、投稿コンテンツをバックアップする義務を負いません。
              </li>
              <li>
                <strong>ユーザーによる投稿コンテンツの変更等</strong>
                <br />
                本サービスにおいて、ユーザーが投稿コンテンツの変更または削除ができる場合におけるユーザーによる投稿コンテンツの変更または削除は、ユーザーがその投稿コンテンツを送受信した際のIDを用いてのみ行えるものとします。
              </li>
              <li>
                <strong>投稿コンテンツの第三者への開示</strong>
                <br />
                投稿コンテンツは、非公開の場合であっても、裁判所、行政機関およびその他の公的機関から要請された場合には開示されることがあります。
              </li>
            </ol>
          </div>

          <div className={styles.termsSection}>
            <h4>9.メッセージ機能</h4>
            <ol>
              <li>ユーザーは、辻本研究室の定める方法により、本サービス上のメッセージ機能を使用することができます。</li>
              <li>
                音声メッセージは、メッセージ機能を通じてデバイスに送受信後、辻本研究室において蓄積することなく、消去されます。
              </li>
              <li>
                テキストメッセージは、メッセージ機能を通じてデバイスに表示した後、一定時間経過後、非表示となります。
              </li>
            </ol>
          </div>

          <div className={styles.termsSection}>
            <h4>10.広告の掲載・送信</h4>
            <p>
              辻本研究室は、本サービスにおいて、ユーザーに対し、辻本研究室もしくは第三者の広告・宣伝のために、ウェブサイト等へ掲載して、広告を掲載もしくは送信できるものとし、ユーザーはこれを予め承諾するものとします。
            </p>
          </div>

          <div className={styles.termsSection}>
            <h4>11.免責事項</h4>
            <ol>
              <li>
                辻本研究室は、本サービスおよび本サービスにおいて提供するアプリケーション、本コンテンツもしくは投稿コンテンツに関して、信頼性、正確性、的確性、適法性、安全性、完全性、権原および非侵害性、ユーザーの特定の利用目的への適合性、本サービスが停止しないこと、動作不良がないこと、常に本サービスを利用できること、本サービスが終了しないこと、セキュリティに関する欠陥がないこと、エラーおよびバグ、第三者の権利侵害がないこと等、その品質および内容、提供状況に関して、何ら保証しません。
              </li>
              <li>
                辻本研究室は、ユーザーがコンピューターウィルスまたは外部からの攻撃等を受けないことを何ら保証しません。
              </li>
              <li>
                辻本研究室は、本サービスを通じて送信または受信される情報が流出、消失または改ざんされないことを何ら保証しません。
              </li>
            </ol>
          </div>

          <div className={styles.termsSection}>
            <h4>12.辻本研究室の賠償責任</h4>
            <p>
              辻本研究室の責任は、その性質を問わず、当社の故意または重過失によらない場合には免責されるものとします。辻本研究室の重過失に起因してユーザーに損害が生じた場合、本サービスの提供に関してユーザーに発生した現実、直接かつ通常の損害（予見可能性の有無を問わず特別損害、逸失利益、データの破損・消失・使用機会の逸失を除きます）に限り、賠償責任を負うものとします。
            </p>
          </div>

          <div className={styles.termsSection}>
            <h4>13.ユーザーの責任</h4>
            <ol>
              <li>
                <strong>本サービス利用に必要なご利用端末・機器等の準備</strong>
                <br />
                本サービスの利用に必要なご利用端末・機器の準備および通信料の負担は、ユーザーの責任において行うものとします。
              </li>
              <li>
                <strong>ユーザーと第三者間の紛争等</strong>
                <br />
                ユーザーは、本サービスの利用において、辻本研究室の責めに帰すべき事由によらないで、第三者と紛争等を生じた場合は、ユーザーの費用と責任において解決しなければならず、辻本研究室はいかなる責任も負いません。
              </li>
            </ol>
          </div>

          <div className={styles.termsSection}>
            <h4>14.禁止行為</h4>
            <p>ユーザーは、本サービスに関連して、以下の行為をしてはなりません。</p>
            <ul>
              <li>法令または公序良俗に反する行為</li>
              <li>辻本研究室または第三者に不利益・迷惑・損害を与える行為</li>
              <li>辻本研究室の許可なく本サービスを第三者へ再提供する行為</li>
              <li>辻本研究室または第三者の知的財産権等を侵害する行為</li>
              <li>犯罪行為、および自殺、自傷等を推奨する行為</li>
              <li>反社会的勢力に関連する行為</li>
              <li>本サービスの運営に過度な負担を与える行為</li>
            </ul>
          </div>

          <div className={styles.termsSection}>
            <h4>15.権利義務譲渡の禁止等</h4>
            <p>
              ユーザーは、本サービスおよび本規約に基づくユーザーの権利義務を第三者に譲渡、承継、換金、貸与または担保の用に供することはできません。
            </p>
          </div>

          <div className={styles.termsSection}>
            <h4>16.本サービスの中断・停止</h4>
            <ol>
              <li>
                辻本研究室は、以下の各号のいずれかに該当すると辻本研究室が判断したときは、本サービスの全部または一部の提供を中断または停止することがあります。
                <ul>
                  <li>
                    地震、火災、落雷、風水害、津波、雪害、その他の天災地変、停電、電力供給の低下、不可避的な事故、法規制、行政指導、行政処分、裁判所の命令、戦争、テロ、内乱、暴動、疫病、その他辻本研究室の支配を超える原因その他の不可抗力により、本サービスの提供を継続することが困難である場合
                  </li>
                  <li>
                    本サービスまたは本サービスの提供に必要な設備・機器・システム・プログラム等の保守、工事等を実施する必要がある場合
                  </li>
                  <li>
                    本サービスまたは本サービスの提供に必要な設備・機器・システム・プログラム等に故障、障害等が発生した場合
                  </li>
                  <li>その他辻本研究室が本サービスの全部または一部の提供を中断する必要があると判断した場合</li>
                </ul>
              </li>
              <li>
                辻本研究室は、前項に基づく本サービスの全部もしくは一部の提供を中断または停止する場合、ユーザーに対して、事前に通知するものとします。ただし、緊急の場合はこの限りではありません。なお、法令その他本規約において別途定める場合を除き、辻本研究室が本サービスの提供を中断または停止するにあたり、辻本研究室はユーザーその他いかなる者に対しても、何らの責任も負わないものとします。
              </li>
            </ol>
          </div>

          <div className={styles.termsSection}>
            <h4>17.本サービスの廃止</h4>
            <ol>
              <li>
                <strong>辻本研究室による本サービスの廃止</strong>
                <br />
                辻本研究室は、以下の各号のいずれかに該当した場合または該当する恐れがあると辻本研究室が合理的に判断した場合、ユーザーに通知することなく、本サービスを廃止することができるものとします。
                <ul>
                  <li>ユーザーが本規約のいずれかに違反した場合</li>
                  <li>ユーザーのIDが失効している場合</li>
                  <li>
                    ユーザーが反社会的勢力の構成員であるもしくは構成員であったこと、またはその関係者であるもしくは関係者であったことが判明した場合
                  </li>
                  <li>
                    ユーザーの利用が辻本研究室もしくは提携先等のサービスの運営に支障をきたす場合、辻本研究室または提携先等のサービスもしくは業務の運営を妨害する場合
                  </li>
                  <li>ユーザーの死亡又は会社の消滅が判明した場合</li>
                  <li>ユーザーが連続する6ヶ月間、IDでログインされない場合</li>
                  <li>ユーザーが、本サービスの廃止をされたことがある場合</li>
                  <li>本サービスの登録内容等に不足または不備もしくは虚偽の内容が含まれることが判明した場合</li>
                  <li>ユーザーが過去に本規約のいずれかに違反したことがある場合</li>
                  <li>ユーザーが未成年である場合で、親権者の同意を得ていないことが判明した場合</li>
                  <li>その他、ユーザーが本サービスを利用することが不適切であると辻本研究室が合理的に判断した場合</li>
                </ul>
              </li>
              <li>
                <strong>ユーザーによる契約の解除</strong>
                <br />
                ユーザーは、本登録を解除しようとする場合は、辻本研究室に申し出て、辻本研究室の承諾を得なければならないものとします。
              </li>
              <li>
                <strong>辻本研究室による本サービスの終了</strong>
                <br />
                辻本研究室は、本サービスの提供を終了する場合、ユーザーに対して事前に周知するものとします。なお、法令その他本規約において別途定める場合を除き、辻本研究室が本サービスの提供を終了するにあたり、辻本研究室はユーザーその他いかなる者に対しても、何らの責任も負わないものとします。
              </li>
            </ol>
          </div>

          <div className={styles.termsSection}>
            <h4>18.本サービスまたは契約の終了後等の取り扱い</h4>
            <ol>
              <li>
                <strong>情報の取り扱い</strong>
                <br />
                辻本研究室は、本サービスの終了後または本登録の終了後（これらの終了の事由を問いません）、別段の定めのない限り、本サービスにおけるユーザーに関する投稿コンテンツ、利用者情報を削除することができ、または引き続き利用もしくは保有することができるものとします。なお、辻本研究室は、当該削除、利用または保有について、いずれの義務も負うものではありません。
              </li>
              <li>
                <strong>システムへの反映</strong>
                <br />
                本サービスに関するシステムの都合上、本サービスの利用停止・終了または本登録の終了（これらの終了等の事由を問いません）の手続きが完了してから、その手続きがシステム上反映されるまで辻本研究室所定の時間が必要な場合があり、ユーザーはこれを予め承諾するものとします。
              </li>
            </ol>
          </div>

          <div className={styles.termsSection}>
            <h4>19.本規約等の変更</h4>
            <ol>
              <li>
                <strong>本規約の変更</strong>
                <br />
                辻本研究室は、本規約の変更が、以下のいずれかの要件を充足する場合には、個別にユーザーと合意することなく、本規約を変更できるものとします。この場合、辻本研究室は、辻本研究室のウェブサイトへの掲載その他相当の方法により、予め周知するものとします。
                <ul>
                  <li>ユーザー一般の利益に適合するとき</li>
                  <li>
                    契約をした目的に反せず、かつ、変更の必要性、変更後の内容の相当性その他の変更に係る事情に照らして合理的なものであるとき
                  </li>
                </ul>
              </li>
              <li>
                <strong>本サービスに関する変更</strong>
                <br />
                辻本研究室は、本サービスの名称、内容および仕様をユーザーの承諾を得ることなく、いつでも任意に変更できるものとします。ただし、本規約の変更が必要な場合は、前項の定めに従うものとします。
              </li>
              <li>
                <strong>プログラム・アプリケーション等の変更</strong>
                <br />
                辻本研究室は、辻本研究室の裁量により、本サービスに関するプログラム・アプリケーション等のバージョンアップ、機能の変更、または利用の制限等（以下「バージョンアップ等」といいます。）を行うことができるものとします。当該プログラム・アプリケーション等のバージョンアップ等が実施された場合、バージョンアップ等の実施前のプログラム・アプリケーション等に対するサポートは辻本研究室の裁量により終了する場合があります。ただし、本規約の変更が必要な場合は、第1項の定めに従うものとします。
              </li>
            </ol>
          </div>

          <div className={styles.termsSection}>
            <h4>20.事業の譲渡等</h4>
            <p>
              辻本研究室は、本規約に基づく権利義務の全部または一部を第三者に承継、売却、事業譲渡、合併、会社分割その他の方法で譲渡できるものとします。この場合、辻本研究室は、本サービスの利用の過程で取得した全ての情報（投稿コンテンツ、ユーザーの個人情報および利用者情報を含みます）を譲渡先に提供できるものとします。
            </p>
          </div>

          <div className={styles.termsSection}>
            <h4>21.一般条項</h4>
            <p>
              <strong>お問合せ先:</strong> contact@metacampus.jp
            </p>
            <p>
              <strong>準拠法:</strong>{" "}
              本規約の解釈およびユーザーの本サービスのご利用にあたっては、日本法が適用されます。
            </p>
            <p>
              <strong>裁判管轄:</strong>{" "}
              本規約および本サービスに関連してユーザーと辻本研究室間で発生した紛争については、東京地方裁判所を第一審の専属的合意管轄裁判所とします。
            </p>
          </div>

          <div className={styles.termsSection}>
            <p className={styles.termsDate}>
              附則（2023年11月1日）
              <br />
              本規約は、2023年11月1日から実施します。
            </p>
          </div>
        </div>
      )
    }
  };

  return (
    <section className={styles.heroAboutSection}>
      {/* Hero Area */}
      <div className={styles.heroArea}>
        {/* 不定形の緑のくぼみ穴 - 右側 */}
        <div
          ref={holeRef}
          className={`${styles.holeContainer} ${holeVisible ? styles.holeAnimate : styles.holeHidden}`}
        >
          <img
            src={HeroArt}
            alt=""
            className={`${styles.holeArt} ${holeVisible ? styles.artAnimate : styles.artHidden}`}
          />
          {/* アニメーションする穴 */}
          <svg className={styles.holeSvg} viewBox="-10 -10 120 120" preserveAspectRatio="none">
            <defs>
              <linearGradient id="holeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#C4E4D2" />
                <stop offset="50%" stopColor="#BEDBE4" />
                <stop offset="100%" stopColor="#C4E4D2" />
              </linearGradient>
              {/* 自然なinner shadow用フィルター */}
              <filter id="innerShadow" x="-50%" y="-50%" width="200%" height="200%">
                <feFlood floodColor="black" floodOpacity="1" result="flood" />
                <feComposite in="flood" in2="SourceAlpha" operator="out" result="outside" />
                <feOffset in="outside" dx="3" dy="3" result="offsetShadow" />
                <feGaussianBlur in="offsetShadow" stdDeviation="4" result="blurShadow" />
                <feComposite in="blurShadow" in2="SourceAlpha" operator="in" result="innerShadow" />
                <feColorMatrix
                  in="innerShadow"
                  type="matrix"
                  result="coloredShadow"
                  values="0 0 0 0 0
                          0 0 0 0 0
                          0 0 0 0 0
                          0 0 0 0.5 0"
                />
                <feMerge>
                  <feMergeNode in="SourceGraphic" />
                  <feMergeNode in="coloredShadow" />
                </feMerge>
              </filter>
            </defs>
            <path fill="url(#holeGradient)" filter="url(#innerShadow)">
              <animate
                attributeName="d"
                dur="20s"
                repeatCount="indefinite"
                values="
                  M 18 12 C 25 -2 42 -2 52 10 S 78 -2 90 18 S 108 48 95 65 S 100 100 82 105 S 48 108 32 88 S -2 98 2 68 S -8 28 18 12 Z;
                  M 15 18 C 28 2 38 5 55 8 S 82 2 92 22 S 105 52 92 70 S 95 105 78 108 S 45 105 28 85 S 2 92 5 62 S -5 32 15 18 Z;
                  M 20 8 C 32 -5 45 0 52 15 S 75 0 88 15 S 110 42 98 62 S 102 98 85 102 S 52 110 35 92 S -5 102 0 72 S -10 25 20 8 Z;
                  M 18 12 C 25 -2 42 -2 52 10 S 78 -2 90 18 S 108 48 95 65 S 100 100 82 105 S 48 108 32 88 S -2 98 2 68 S -8 28 18 12 Z
                "
              />
            </path>
          </svg>
        </div>
        <div className={styles.heroContent}>
          <div className={styles.heroText}>
            <h1 className={styles.heroTitle}>
              やりたいが、カタチになる。
              <br />
              共想から共創が生まれるメタバースキャンパス
            </h1>
            <button onClick={handleCTAClick} className={styles.ctaButton}>
              エントランスワールドへ <span className={styles.ctaArrow}>&gt;</span>
            </button>
          </div>
        </div>
      </div>

      {/* About Area */}
      <div ref={aboutAreaRef} className={styles.aboutArea}>
        <div ref={aboutRef} className={styles.aboutContainer}>
          <div
            className={`${styles.aboutImageArea} ${aboutVisible ? styles.cardAnimate : styles.cardHidden}`}
            style={{ animationDelay: "0s" }}
          >
            <video autoPlay loop muted playsInline preload="metadata" className={styles.aboutImage}>
              <source src={BackgroundWebM} type="video/webm" />
              <source src={BackgroundMP4} type="video/mp4" />
            </video>
          </div>
          <div
            className={`${styles.aboutTextArea} ${aboutVisible ? styles.cardAnimate : styles.cardHidden}`}
            style={{ animationDelay: "0.15s" }}
          >
            <h2 className={styles.aboutTitle}>MetaCampUsとは？</h2>
            <p className={styles.aboutDescription}>学び、探求し、創っていく。</p>
            <p className={styles.aboutDescription}>
              MetaCampUsは、大学の枠を超えて新しい知と創造を育む、 オンライン上の共創コミュニティです。
            </p>
            <button className={styles.aboutButton} onClick={handleExpandClick}>
              {isExpanded ? "閉じる" : "詳しく見る"}{" "}
              <span className={`${styles.aboutArrow} ${isExpanded ? styles.arrowRotated : ""}`}>
                {isExpanded ? "+" : ">"}
              </span>
            </button>
          </div>
        </div>

        {/* Expanded Content */}
        <div className={`${styles.expandedSection} ${isExpanded ? styles.expanded : ""}`}>
          <div className={styles.expandedContent}>
            {/* Tab Navigation */}
            <div className={styles.tabNav}>
              <button
                className={`${styles.tabButton} ${activeTab === "about" ? styles.tabActive : ""}`}
                onClick={() => setActiveTab("about")}
              >
                コンセプト
              </button>
              <button
                className={`${styles.tabButton} ${activeTab === "guide" ? styles.tabActive : ""}`}
                onClick={() => setActiveTab("guide")}
              >
                操作説明
              </button>
              <button
                className={`${styles.tabButton} ${activeTab === "faq" ? styles.tabActive : ""}`}
                onClick={() => setActiveTab("faq")}
              >
                よくある質問
              </button>
              <button
                className={`${styles.tabButton} ${activeTab === "terms" ? styles.tabActive : ""}`}
                onClick={() => setActiveTab("terms")}
              >
                利用規約
              </button>
            </div>

            {/* Tab Content */}
            <div className={styles.tabPanel}>{tabContents[activeTab].content}</div>
          </div>
        </div>
      </div>
    </section>
  );
}
